// --- Addon Price Integration ---
let currentAddonPriceData = {
  price: 0,        // in cents
  fullPrice: 0,    // in cents
  isChecked: false,
  // Add a flag to know if the call is from the addon toggle
  triggeredByAddonToggle: false
};

document.addEventListener('addon:toggled', (event) => {
  const { price, fullPrice, isChecked } = event.detail;
  // --- DEBUGGING POINT ---
  // Log the raw price data received from the addon toggle event.
  // This is crucial to verify if 'price' and 'fullPrice' are single addon unit prices
  // or if they might have been pre-multiplied elsewhere.
  console.log('[Addon Event Received]', { rawPrice: price, rawFullPrice: fullPrice, isChecked });

  currentAddonPriceData.price = isChecked ? price : 0; // This should be the price for ONE unit of the addon
  currentAddonPriceData.fullPrice = isChecked ? fullPrice : 0; // This should be the full price for ONE unit of the addon
  currentAddonPriceData.isChecked = isChecked;
  currentAddonPriceData.triggeredByAddonToggle = true; // Set the flag for contextual logging in updateFormFromSelectedOption

  // Attempt to re-trigger the price update.
  updateFormFromSelectedOption();

  currentAddonPriceData.triggeredByAddonToggle = false; // Reset the flag after use
});

// --- End Addon Price Integration ---

class ServingOption extends HTMLElement {
  constructor() {
    super();
    this.currentOption = null;
    this.isLoading = false;
    this.initialized = false;
    this.preventRecursion = false;
    this.options = null;
    this.storedHeaders = {
      optionsHeader: null,
      servingOptionTitle: null,
      subscribeHeader: null,
      subscribeHeaderSubscription: null,
      subscribeHeaderOtp: null
    };
    this.freeShippingState = {
      isEnabled: false,
      showUnderPill: false,
      showNextToTitle: false,
      showInOptions: false,
      freeShippingText: 'Free shipping. Pause or cancel anytime.',
      originalLocation: 'none' // Track where it was originally found
    };
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const optionsId = this.getAttribute('data-options-id');
    if (!optionsId) return;

    const optionsScript = document.getElementById(optionsId);
    if (!optionsScript) return;

    try {
      this.options = JSON.parse(optionsScript.textContent.trim());
      if (!this.currentOption) {
        this.currentOption = this.options[0];
      }
      this.storeHeaders();
      this.storeFreeShippingState();
      this.render();
      this.setupEventListeners();
      this.setupTabEventListeners();
    } catch (error) {
      console.error('Error parsing serving options:', error);
    }
  }

  storeHeaders() {
    const optionsHeader = document.querySelector('.options-header');
    const servingOptionTitle = document.querySelector(
      '.product-card__serving-option-title'
    );
    const subscribeHeader = document.querySelector('.subscribe-header');
    const subscribeHeaderSubscription = document.querySelector(
      '.subscribe-header--subscription'
    );
    const subscribeHeaderOtp = document.querySelector('.subscribe-header--otp');

    if (optionsHeader)
      this.storedHeaders.optionsHeader = optionsHeader.innerHTML;
    if (servingOptionTitle)
      this.storedHeaders.servingOptionTitle = servingOptionTitle.innerHTML;
    if (subscribeHeader)
      this.storedHeaders.subscribeHeader = subscribeHeader.innerHTML;
    if (subscribeHeaderSubscription)
      this.storedHeaders.subscribeHeaderSubscription =
        subscribeHeaderSubscription.innerHTML;
    if (subscribeHeaderOtp)
      this.storedHeaders.subscribeHeaderOtp = subscribeHeaderOtp.innerHTML;
  }

  storeFreeShippingState() {
    // Initialize the state object with more detailed tracking
    this.freeShippingState = {
      isEnabled: false,
      showUnderPill: false,
      showNextToTitle: false,
      showInOptions: false,
      freeShippingText: 'Free shipping. Pause or cancel anytime.',
      originalLocation: 'none' // Track where it was originally found
    };

    // Check for free shipping under subscribe pill first (highest priority)
    const subscribePill = document.querySelector('.tab-option[data-tab="subscribe"]');
    const freeShippingUnderPill = subscribePill?.querySelector('.option-free-shipping');

    if (freeShippingUnderPill) {
      this.freeShippingState.isEnabled = true;
      this.freeShippingState.showUnderPill = true;
      this.freeShippingState.originalLocation = 'under-pill';
      this.freeShippingState.freeShippingText = freeShippingUnderPill.textContent.trim();
      return; // Found it, no need to check other locations
    }

    // Check for free shipping next to title (in subscription header)
    const subscribeHeader = document.querySelector('.subscribe-header--subscription');
    const freeShippingNextToTitle = subscribeHeader?.querySelector('.option-free-shipping--icon');

    if (freeShippingNextToTitle) {
      this.freeShippingState.isEnabled = true;
      this.freeShippingState.showNextToTitle = true;
      this.freeShippingState.originalLocation = 'next-to-title';
      this.freeShippingState.freeShippingText = freeShippingNextToTitle.textContent.trim();
      return; // Found it, no need to check delivery options
    }

    // Check for free shipping in delivery options (lowest priority)
    const deliveryOptionWithShipping = document.querySelector('.delivery-option .option-free-shipping');

    if (deliveryOptionWithShipping) {
      this.freeShippingState.isEnabled = true;
      this.freeShippingState.showInOptions = true;
      this.freeShippingState.originalLocation = 'in-options';
      this.freeShippingState.freeShippingText = deliveryOptionWithShipping.textContent.trim();

      // Store which specific options originally had free shipping
      this.freeShippingState.originalOptionsWithShipping = [];
      const allOptionsWithShipping = document.querySelectorAll('.delivery-option');
      allOptionsWithShipping.forEach(option => {
        const hasShippingText = option.querySelector('.option-free-shipping');
        const hasShippingData = option.getAttribute('data-has-free-shipping') === 'true';
        if (hasShippingText || hasShippingData) {
          const productId = option.getAttribute('data-product-id');
          const sellingPlan = option.getAttribute('data-selling-plan');
          this.freeShippingState.originalOptionsWithShipping.push({
            productId,
            sellingPlan,
            hasText: !!hasShippingText
          });
        }
      });
    }
  }

  /**
   * Restores free shipping text to its original location based on stored state
   * @param {HTMLElement} purchaseBlock - The purchase block container
   */
  restoreFreeShippingText(purchaseBlock) {
    // Clear any existing free shipping text to avoid duplication
    this.clearAllFreeShippingText(purchaseBlock);

    switch (this.freeShippingState.originalLocation) {
      case 'under-pill':
        this.addFreeShippingUnderPill(purchaseBlock);
        break;
      case 'next-to-title':
        this.addFreeShippingNextToTitle(purchaseBlock);
        break;
      case 'in-options':
        this.addFreeShippingInOptions(purchaseBlock);
        break;
    }
  }

  /**
   * Clears all free shipping text from all possible locations
   * @param {HTMLElement} purchaseBlock - The purchase block container
   */
  clearAllFreeShippingText(purchaseBlock) {
    // Remove from subscribe pill
    const subscribePill = purchaseBlock.querySelector('.tab-option[data-tab="subscribe"]');
    const pillShippingText = subscribePill?.querySelector('.option-free-shipping');
    if (pillShippingText) pillShippingText.remove();

    // Remove from delivery options
    const deliveryOptionShippingTexts = purchaseBlock.querySelectorAll('.delivery-option .option-free-shipping');
    deliveryOptionShippingTexts.forEach((el) => el.remove());

    // Note: We don't remove from next-to-title location as it's part of the Liquid template
    // and gets replaced when new content is loaded
  }

  /**
   * Adds free shipping text under the subscribe pill
   * @param {HTMLElement} purchaseBlock - The purchase block container
   */
  addFreeShippingUnderPill(purchaseBlock) {
    const subscribePill = purchaseBlock.querySelector('.tab-option[data-tab="subscribe"]');
    if (!subscribePill) return;

    subscribePill.insertAdjacentHTML(
      'beforeend',
      `<div class="option-free-shipping">${this.freeShippingState.freeShippingText}</div>`
    );

    // Ensure proper styling for the tab selector
    const tabSelector = subscribePill.closest('.tab-selector');
    if (tabSelector) {
      tabSelector.classList.add('purchase-block__list-option');
      if (!tabSelector.classList.contains('tab-selector')) {
        tabSelector.classList.add('tab-selector');
      }
    }
  }

  /**
   * Adds free shipping text next to the title (this location is handled by Liquid template)
   * @param {HTMLElement} purchaseBlock - The purchase block container
   */
  addFreeShippingNextToTitle(purchaseBlock) {
    // This location is typically handled by the Liquid template based on block settings
    // If the new product content doesn't have it, we need to add it manually
    const subscribeHeader = purchaseBlock.querySelector('.subscribe-header--subscription');
    if (!subscribeHeader) return;

    let freeShippingElement = subscribeHeader.querySelector('.option-free-shipping--icon');
    if (!freeShippingElement) {
      subscribeHeader.insertAdjacentHTML(
        'beforeend',
        `<div class="option-free-shipping option-free-shipping--icon">${this.freeShippingState.freeShippingText}</div>`
      );
    }
  }

  /**
   * Adds free shipping text to delivery options that originally had it
   * @param {HTMLElement} purchaseBlock - The purchase block container
   */
  addFreeShippingInOptions(purchaseBlock) {
    if (!this.freeShippingState.originalOptionsWithShipping) return;

    const deliveryOptions = purchaseBlock.querySelectorAll('.delivery-option');
    deliveryOptions.forEach((option) => {
      const productId = option.getAttribute('data-product-id');
      const sellingPlan = option.getAttribute('data-selling-plan');
      const hasShippingData = option.getAttribute('data-has-free-shipping') === 'true';

      // Check if this option should have free shipping based on original state or data attribute
      const shouldHaveShipping = hasShippingData ||
        this.freeShippingState.originalOptionsWithShipping.some(original =>
          original.productId === productId && original.sellingPlan === sellingPlan
        );

      if (shouldHaveShipping) {
        const optionDetails = option.querySelector('.option-details');
        if (optionDetails && !optionDetails.querySelector('.option-free-shipping')) {
          optionDetails.insertAdjacentHTML(
            'beforeend',
            `<div class="option-free-shipping">${this.freeShippingState.freeShippingText}</div>`
          );
        }
      }
    });
  }

  setupTabEventListeners() {
    const purchaseBlock = this.parentElement.closest(
      '.product-card__purchase-block'
    );
    if (!purchaseBlock) return;

    const tabOptions = purchaseBlock.querySelectorAll('.tab-option');
    tabOptions.forEach((tab) => {
      tab.addEventListener('click', () =>
        this.toggleSubscribeHeaders(tab.dataset.tab)
      );
    });
  }

  toggleSubscribeHeaders(tabType) {
    const subscribeHeaderSubscription = document.querySelector(
      '.subscribe-header--subscription'
    );
    const subscribeHeaderOtp = document.querySelector('.subscribe-header--otp');

    if (!subscribeHeaderSubscription || !subscribeHeaderOtp) return;

    if (tabType === 'subscribe') {
      subscribeHeaderSubscription.classList.remove('hidden');
      subscribeHeaderOtp.classList.add('hidden');
    } else if (tabType === 'onetime') {
      subscribeHeaderSubscription.classList.add('hidden');
      subscribeHeaderOtp.classList.remove('hidden');
    }
  }

  render() {
    if (!this.options) return;

    this.innerHTML = `
          ${this.options
      .map(
        (option, index) => `
            <button 
              class="serving-option__button purchase-block__option ${
          this.currentOption === option ? 'active' : ''
        }"
              role="tab"
              aria-selected="${this.currentOption === option}"
              aria-controls="serving-panel-${index}"
              data-index="${index}"
              ${
          this.currentOption === option ? 'tabindex="0"' : 'tabindex="-1"'
        }
            >
              <div class="serving-option__content">
                <span class="serving-option__title">${option.title}</span>
              </div>
            </button>
          `
      )
      .join('')}
    `;
  }

  setupEventListeners() {
    this.querySelectorAll('.serving-option__button').forEach((button) => {
      button.addEventListener('click', (e) => this.handleOptionClick(e));
      button.addEventListener('keydown', (e) => this.handleKeydown(e));
    });
  }

  async handleOptionClick(event) {
    const button = event.currentTarget;
    const index = parseInt(button.dataset.index);
    const selectedOption = this.options[index];

    if (
      this.isLoading ||
      this.currentOption === selectedOption ||
      this.preventRecursion
    )
      return;

    this.updateActiveButton(button);
    this.currentOption = selectedOption;

    // Only update loading state in the button
    button.classList.add('is-loading');

    await this.loadProductContent(selectedOption);

    button.classList.remove('is-loading');
  }

  handleKeydown(event) {
    const buttons = Array.from(
      this.querySelectorAll('.serving-option__button')
    );
    const index = buttons.indexOf(event.currentTarget);
    let newIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        newIndex = (index + 1) % buttons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        newIndex = (index - 1 + buttons.length) % buttons.length;
        break;
      default:
        return;
    }

    event.preventDefault();
    buttons[newIndex].click();
    buttons[newIndex].focus();
  }

  updateActiveButton(newActiveButton) {
    this.querySelectorAll('.serving-option__button').forEach((button) => {
      const isActive = button === newActiveButton;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-selected', isActive);
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  async loadProductContent(option) {
    if (!option.handle) return;
    this.isLoading = true;

    const purchaseBlock = this.parentElement.closest(
      '.product-card__purchase-block'
    );
    const productContent = purchaseBlock.parentElement;
    if (!productContent || !purchaseBlock) return;

    try {
      const response = await fetch(
        `/products/${option.handle}?section_id=product-purchase-options`
      );
      if (!response.ok) throw new Error('Product fetch failed');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newContent = doc.querySelector('[data-api-content]');
      if (!newContent) return;

      const currentServingOptions = this.closest(
        '.product-card__serving-options'
      );
      const servingOptionsHtml = currentServingOptions.outerHTML;

      const newGiftContent = newContent.querySelector('.gift-list');
      const newGiftHtml = newGiftContent ? newGiftContent.outerHTML : '';

      const tempContent = document.createElement('div');
      tempContent.style.position = 'absolute';
      tempContent.style.top = '0';
      tempContent.style.left = '0';
      tempContent.style.width = '100%';
      tempContent.style.opacity = '0';
      tempContent.innerHTML = newContent.innerHTML;

      const newPurchaseBlock = tempContent.querySelector(
        '.product-card__purchase-block'
      );
      if (newPurchaseBlock) {
        const newServingOptions = newPurchaseBlock.querySelector(
          '.product-card__serving-options'
        );
        if (newServingOptions) {
          newServingOptions.remove();
        }

        const newForm = newPurchaseBlock.querySelector('product-form form');
        const newVariantId = newForm?.querySelector('input[name="id"]')?.value;

        // Save the price element content
        const priceElements = newPurchaseBlock.querySelectorAll(
          '[data-js-product-price-extended]'
        );
        let priceElementHTML = '';
        if (priceElements.length > 0) {
          priceElementHTML = priceElements[0].innerHTML;
        }

        const purchaseBlockWrapper = purchaseBlock.querySelector(
          '.product-card__purchase-block-wrapper'
        );
        if (purchaseBlockWrapper) {
          const newPurchaseBlockWrapper = newPurchaseBlock.querySelector(
            '.product-card__purchase-block-wrapper'
          );
          if (newPurchaseBlockWrapper) {
            const existingWrapper = purchaseBlockWrapper;
            const newWrapperContent = newPurchaseBlockWrapper.innerHTML;

            existingWrapper.innerHTML = '';

            if (newGiftHtml) {
              const productGiftBlock =
                purchaseBlock.querySelector('.gift-list');
              if (productGiftBlock) {
                productGiftBlock.outerHTML = newGiftHtml;
              } else {
                purchaseBlock.insertAdjacentHTML('beforeend', newGiftHtml);
              }
            }
            existingWrapper.insertAdjacentHTML('beforeend', servingOptionsHtml);
            existingWrapper.insertAdjacentHTML('beforeend', newWrapperContent);
          } else {
            purchaseBlockWrapper.innerHTML =
              newGiftHtml + servingOptionsHtml + newPurchaseBlock.innerHTML;
          }
        } else {
          purchaseBlock.innerHTML =
            newGiftHtml + servingOptionsHtml + newPurchaseBlock.innerHTML;
        }

        // Update headers with stored values
        const optionsHeader = document.querySelector('.options-header');
        const servingOptionTitle = document.querySelector(
          '.product-card__serving-option-title'
        );
        const subscribeHeader = document.querySelector('.subscribe-header');
        const subscribeHeaderSubscription = document.querySelector(
          '.subscribe-header--subscription'
        );
        const subscribeHeaderOtp = document.querySelector(
          '.subscribe-header--otp'
        );

        if (optionsHeader && this.storedHeaders.optionsHeader) {
          optionsHeader.innerHTML = this.storedHeaders.optionsHeader;
        }
        if (servingOptionTitle && this.storedHeaders.servingOptionTitle) {
          servingOptionTitle.innerHTML = this.storedHeaders.servingOptionTitle;
        }
        if (subscribeHeader && this.storedHeaders.subscribeHeader) {
          subscribeHeader.innerHTML = this.storedHeaders.subscribeHeader;
        }
        if (
          subscribeHeaderSubscription &&
          this.storedHeaders.subscribeHeaderSubscription
        ) {
          subscribeHeaderSubscription.innerHTML =
            this.storedHeaders.subscribeHeaderSubscription;
        }
        if (subscribeHeaderOtp && this.storedHeaders.subscribeHeaderOtp) {
          subscribeHeaderOtp.innerHTML = this.storedHeaders.subscribeHeaderOtp;
        }

        // Apply saved price element content to all price elements
        if (priceElementHTML) {
          const currentPriceElements = purchaseBlock.querySelectorAll(
            '[data-js-product-price-extended]'
          );
          currentPriceElements.forEach((el) => {
            el.innerHTML = priceElementHTML;
          });
        }

        // Restore free shipping text state - only in the location it was originally found
        if (this.freeShippingState.isEnabled) {
          this.restoreFreeShippingText(purchaseBlock);
        }

        // Check active tab and apply header visibility
        const activeTab = purchaseBlock.querySelector('.tab-option.active');
        if (activeTab) {
          this.toggleSubscribeHeaders(activeTab.dataset.tab);
        }

        const servingOption = purchaseBlock.querySelector('serving-option');
        if (servingOption) {
          this.preventRecursion = true;

          const currentOptions = this.options;
          const currentSelectedOption = this.currentOption;

          servingOption.initialized = false;
          servingOption.options = currentOptions;
          servingOption.currentOption = currentSelectedOption;
          servingOption.storedHeaders = this.storedHeaders;
          servingOption.freeShippingState = this.freeShippingState;
          servingOption.connectedCallback();

          const buttons = servingOption.querySelectorAll(
            '.serving-option__button'
          );
          const activeButton =
            buttons[this.options.indexOf(this.currentOption)];
          if (activeButton) {
            servingOption.updateActiveButton(activeButton);
          }

          this.preventRecursion = false;
        }

        // Reinitialize subscription tabs
        const subscriptionTabs = purchaseBlock.querySelector('subscription-tabs');
        if (subscriptionTabs) {
          const oldTabs = subscriptionTabs.querySelectorAll('[data-tab]');
          oldTabs.forEach((tab) => {
            tab.replaceWith(tab.cloneNode(true));
          });

          // Reinitialize the tabs by calling connectedCallback
          subscriptionTabs.connectedCallback();

          // Reattach event listeners for tab switching
          const newServingOption =
            purchaseBlock.querySelector('serving-option');
          if (newServingOption) {
            newServingOption.setupTabEventListeners();
          }
        }

        // Reinitialize add to cart functionality
        const product_options =
          purchaseBlock.querySelectorAll('.delivery-option');
        const radio_inputs = purchaseBlock.querySelectorAll(
          '.delivery-option input[type="radio"]'
        );

        // Reset event listeners for delivery options
        product_options.forEach((option) => {
          option.addEventListener('click', function(e) {
            option.querySelector('.option-radio').checked = true;
            option
              .querySelector('.option-radio')
              .dispatchEvent(new Event('change', { bubbles: true }));
            updateFormFromSelectedOption();
          });
        });

        // Reset event listeners for radio inputs
        radio_inputs.forEach((radio) => {
          radio.addEventListener('change', function(e) {
            updateFormFromSelectedOption(this);
          });
        });

        // Reinitialize ProductForm custom element
        const productForm = purchaseBlock.querySelector('product-form');
        if (productForm) {
          // Force recreate the ProductForm custom element by cloning it
          const newProductForm = productForm.cloneNode(true);
          productForm.parentNode.replaceChild(newProductForm, productForm);

          // The custom element will automatically reinitialize when inserted into DOM
          // since the constructor runs again with the new element
        }

        // Initialize first option
        const firstOption = purchaseBlock.querySelector(
          '.subscription-content:not(.hidden) .delivery-option input[type="radio"]'
        );
        if (firstOption) {
          firstOption.checked = true;
          firstOption.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Dispatch event to notify other components of the variant change
        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: { variantId: newVariantId }
          })
        );

        // Trigger form update
        updateFormFromSelectedOption();
      }
    } catch (error) {
      console.error('Error loading product content:', error);
      productContent.innerHTML = `
        <div class="serving-panel__error">
          <p>There was an error loading the product. Please try again.</p>
        </div>
      `;
    } finally {
      this.isLoading = false;
      this.preventRecursion = false;

      const atcButtons = purchaseBlock.querySelectorAll('.NEW-CUSTOM-ATC');
      console.log(atcButtons);
      atcButtons.forEach(button => {
        button.addEventListener('click', function(e) {
          e.preventDefault();
          // Get selected product
          let selectedOption = document.querySelector('.subscription-content:not(.hidden) .delivery-option input:checked');
          let selectedOptionData = selectedOption.parentNode;
          if (!selectedOption) return;

          const product = {
            id: selectedOptionData.getAttribute('data-product-id'),
            variant: selectedOptionData.getAttribute('data-product-id'),
            sellingPlanId: selectedOptionData.getAttribute('data-selling-plan') || '',
            properties: {}
          };

          // Get quantity
          let quantity = 1;
          const quantityInput = document.querySelector('#quantity') ||
            selectedOption.querySelector('#custom_quantity_count');
          if (quantityInput) {
            quantity = parseInt(quantityInput.value, 10) || 1;
          }

          // Add to cart
          addToCart(product, quantity);
        });
      });
    }
  }
}

if (!customElements.get('serving-option')) {
  customElements.define('serving-option', ServingOption);
}

// Helper function to update form from selected option
function updateFormFromSelectedOption(product_option_selected = null) {
  // If no option provided, find the checked one
  if (!product_option_selected) {
    product_option_selected = document.querySelector('.subscription-content:not(.hidden) .delivery-option input:checked');
    if (!product_option_selected) {
      if (currentAddonPriceData.triggeredByAddonToggle) {
        console.log('[ADDON_TOGGLE_CONTEXT] updateFormFromSelectedOption: No product_option_selected found! This might be okay if only addon changed.');
      }
      // If only the addon changed, and no main product is selected, we might still want to update
      // price if a default or previously selected main product context exists.
      // For now, original behavior: exit if no selected option found.
      return;
    }
  }

  if (currentAddonPriceData.triggeredByAddonToggle) {
    console.log('[ADDON_TOGGLE_CONTEXT] updateFormFromSelectedOption: Called.');
    console.log('[ADDON_TOGGLE_CONTEXT] Selected main product option element:', product_option_selected);
    const parentOptionData = product_option_selected.closest('.delivery-option');
    if (parentOptionData) {
      console.log('[ADDON_TOGGLE_CONTEXT] Main product data attributes:', {
        productOtp: parentOptionData.dataset.productOtp,
        quantity: parentOptionData.dataset.quantity,
        price: parentOptionData.dataset.price,
        fullPrice: parentOptionData.dataset.fullPrice,
        packSize: parentOptionData.dataset.packSize
      });
    }
  }

  let product_form = document.querySelector('.product-form');
  let productOptionSelectedData = product_option_selected.closest('.delivery-option');

  // Get the product and variant IDs
  const productId = productOptionSelectedData.parentNode.getAttribute('data-product-id');
  const variantId = productOptionSelectedData.parentNode.getAttribute('data-variant-id') || productId;
  const sellingPlanId = productOptionSelectedData.parentNode.getAttribute('data-selling-plan');
  const sellingPlanName = productOptionSelectedData.parentNode.getAttribute('data-selling-plan-name');

  const isOtp = productOptionSelectedData.parentNode.dataset.productOtp === 'true';
  let mainProductQuantity, packQuantity;

  // Determine mainProductQuantity based on OTP or Subscription
  if (isOtp) {
    mainProductQuantity = parseInt(productOptionSelectedData.parentNode.dataset.quantity || '1', 10);
  } else { // Subscription
    mainProductQuantity = 1;
    packQuantity = parseInt(productOptionSelectedData.parentNode.dataset.packSize || '1', 10);
  }

  // Update the form's quantity input
  product_form.querySelector('input[name="quantity"]').value = mainProductQuantity;

  // Get raw prices from attributes for the main product
  const rawPriceInCents = parseFloat(productOptionSelectedData.parentNode.getAttribute('data-price') || '0');
  const rawCompareAtPriceInCents = parseFloat(productOptionSelectedData.parentNode.getAttribute('data-full-price') || '0');

  let mainProductTotalSellingPriceInCents;
  let mainProductTotalCompareAtPriceBaseInCents;

  // Calculate total price for the main product based on its quantity and pricing structure (OTP vs. Subscription)
  if (isOtp) {
    // For OTP: data-price is assumed to be the TOTAL selling price for the 'mainProductQuantity' items.
    mainProductTotalSellingPriceInCents = rawPriceInCents;

    // For OTP Compare-at price: data-full-price is assumed to be PER ITEM.
    // Multiply by mainProductQuantity to get the total compare-at price for the pack.
    if (rawCompareAtPriceInCents > 0) {
      const otpTotalCalculatedCompareAt = rawCompareAtPriceInCents * mainProductQuantity;
      if (otpTotalCalculatedCompareAt > mainProductTotalSellingPriceInCents) {
        mainProductTotalCompareAtPriceBaseInCents = otpTotalCalculatedCompareAt;
      } else {
        mainProductTotalCompareAtPriceBaseInCents = mainProductTotalSellingPriceInCents;
      }
    } else {
      mainProductTotalCompareAtPriceBaseInCents = mainProductTotalSellingPriceInCents;
    }
  } else { // Subscription
    // For Subscriptions: data-price is PER ITEM and mainProductQuantity is packSize.
    mainProductTotalSellingPriceInCents = rawPriceInCents;

    // Assume data-full-price for subscription is also PER ITEM.
    if (rawCompareAtPriceInCents > 0 && rawCompareAtPriceInCents > rawPriceInCents) {
      mainProductTotalCompareAtPriceBaseInCents = rawCompareAtPriceInCents * packQuantity;
      console.log(mainProductTotalCompareAtPriceBaseInCents, 'mainProductTotalCompareAtPriceBaseInCents');
    } else {
      mainProductTotalCompareAtPriceBaseInCents = mainProductTotalSellingPriceInCents;
    }
  }

  // Initialize final prices with main product totals
  let finalTotalSellingPriceInCents = mainProductTotalSellingPriceInCents;
  let finalTotalCompareAtPriceInCents = mainProductTotalCompareAtPriceBaseInCents;

  if (isOtp) {
    DisplayPrice(
      finalTotalSellingPriceInCents / 100,
      (finalTotalCompareAtPriceInCents > finalTotalSellingPriceInCents) ? finalTotalCompareAtPriceInCents / 100 : undefined,
      mainProductQuantity,
      productId,
      currentAddonPriceData.price / 100
    );
  } else {
    DisplayPrice(
      finalTotalSellingPriceInCents / 100,
      (finalTotalCompareAtPriceInCents > finalTotalSellingPriceInCents) ? finalTotalCompareAtPriceInCents / 100 : undefined,
      packQuantity,
      productId,
      currentAddonPriceData.price / 100
    );
  }


  // Update form values
  product_form.querySelector('input[name="id"]').value = variantId;
  product_form.querySelector('input[name="selling_plan"]').value = sellingPlanId;
  product_form.querySelector('input[name="option-0"]').value = sellingPlanName;

  // Handle free shipping display based on data-has-free-shipping attribute
  handleFreeShippingDisplay(productOptionSelectedData.parentNode);

  // Dispatch event to update product display
  const event = new CustomEvent('product:variant:change', {
    detail: {
      productId: productId,
      variantId: variantId,
      sellingPlanId: sellingPlanId,
      sellingPlanName: sellingPlanName
    }
  });
  document.dispatchEvent(event);
}

// Function to handle free shipping display based on data-has-free-shipping attribute
function handleFreeShippingDisplay(selectedOptionElement) {
  if (!selectedOptionElement) return;

  const hasDataFreeShipping = selectedOptionElement.getAttribute('data-has-free-shipping') === 'true';
  const purchaseBlock = selectedOptionElement.closest('.product-card__purchase-block');

  if (!purchaseBlock) return;

  // Find all existing free shipping elements
  const existingFreeShippingElements = purchaseBlock.querySelectorAll('.option-free-shipping');

  if (hasDataFreeShipping) {
    // Show free shipping elements if they exist
    existingFreeShippingElements.forEach(element => {
      element.style.display = '';
      element.classList.remove('hidden');
    });
  } else {
    // Hide free shipping elements if the selected option doesn't have data-has-free-shipping="true"
    existingFreeShippingElements.forEach(element => {
      element.style.display = 'none';
      element.classList.add('hidden');
    });
  }
}
